import moment from 'moment';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { AusenciasProvider } from '../../ausencias/Context';
import { Agendamento } from '../Enum';
import { HorariosAtendenteService } from '../Service';
import { WebSocketManager } from '../../../WebSocketConfig';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { AtendentesService } from '../../atendentes/Service';
import { useFilterApp } from '../../../contexts/FilterApp';

const atendentesService = new AtendentesService();
const horariosService = new HorariosAtendenteService();
const REFRESH_INTERVAL_MS = 3 * 60 * 1000;

interface AgendaParams {
    loading: boolean;
    date: moment.Moment;
    schedules: Agendamento[] | undefined;
    selectedSchedule: Agendamento | undefined;
    selectedRange: { start: number; end: number } | undefined;
    setSelectedRange: React.Dispatch<React.SetStateAction<{ start: number; end: number } | undefined>>;
    settingsAgenda: any
    loadSettings: any
    attendantId: string
    reloadSchedules: any
}

const AgendaContext = createContext<AgendaParams | undefined>(undefined);

export const useAgendaContext = () => {
    const context = useContext(AgendaContext);
    if (!context) {
        throw new Error('useAgendaContext must be used within an AgendaProvider');
    }
    return context;
};

export const useOptionalAgendaContext = () => {
    return useContext(AgendaContext);
};

export const AgendaProvider: React.FC<{ date: string; children: React.ReactNode }> = ({ date, children }) => {
    const { action, id } = useParamsContext();
    const { estabSession } = useEstabsContext();
    const [loading, setLoading] = useState(true);
    const {attendantId} = useFilterApp()
    const [schedules, setSchedules] = useState<Agendamento[] | undefined>(undefined);
    const [selectedSchedule, setSelectedSchedule] = useState<Agendamento | undefined>(undefined);
    const [selectedRange, setSelectedRange] = useState<{ start: number; end: number } | undefined>();
    const [settingsAgenda, setSettingsAgenda] = useState<any>(undefined);
    const currentDateRef = useRef(date);

    const fetchSchedules = useCallback(async (date: moment.Moment) => {
        try {
            const { data } = await horariosService.getHorariosCards(date.toDate(), attendantId);
            setSchedules(data);
        } catch (error) {
            console.log('Error fetching schedules:', error);
        } finally {
            setLoading(false);
        }
    }, [attendantId]);

    const loadSettings = async () => {
        try {
            setLoading(true);

            const { data } = await atendentesService.getAgendaDate(attendantId, date);
            setSettingsAgenda(data);
        } catch (e) {
            console.error('Erro ao carregar configurações:', e);
        } finally {
            setLoading(false);
        }
    };

    const reloadSchedules = () => {
        fetchSchedules(moment(date));
    }

    useEffect(() => {
        fetchSchedules(moment(date));
        loadSettings();
    }, [date, attendantId, fetchSchedules]);

    useEffect(() => {
        currentDateRef.current = date;
    }, [date]);

    // O atendimento aberto guarda a copia do momento do clique; ao recarregar a lista, passa a usar a versao nova.
    useEffect(() => {
        if (!selectedSchedule || !schedules) return;
        const fresh = schedules.find((e) => e._id === selectedSchedule._id);
        if (fresh && fresh !== selectedSchedule) setSelectedSchedule(fresh);
    }, [schedules, selectedSchedule]);

    useEffect(() => {
        if (!estabSession?._id) return;

        const webSocketManager = new WebSocketManager();
        webSocketManager.onReady(() => {
            webSocketManager.onChannel(`horarios:atendentes:${estabSession._id}`, () => {
                fetchSchedules(moment(currentDateRef.current));
            });
        });

        return () => {
            webSocketManager.disconnect();
        };
    }, [attendantId, estabSession?._id, fetchSchedules]);

    useEffect(() => {
        if (!attendantId) return;

        const intervalId = window.setInterval(() => {
            fetchSchedules(moment(currentDateRef.current));
        }, REFRESH_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [attendantId, fetchSchedules]);

    useEffect(() => {
        if (action === 'view' && !!id) {
            setSelectedSchedule(schedules?.find((e) => e._id === id));
        } else {
            setSelectedSchedule(undefined);
        }
    }, [action, id, schedules]);

    return (
        <AgendaContext.Provider
            value={{
                loading,
                schedules,
                selectedSchedule,
                date: moment(date),
                selectedRange,
                setSelectedRange,
                settingsAgenda,
                loadSettings,
                attendantId,
                reloadSchedules
            }}
        >
            <AusenciasProvider>{children}</AusenciasProvider>
        </AgendaContext.Provider>
    );
};
